/* Gentle Beauty — shared page utilities (TOC + scroll-spy, decorative parallax, story timeline, clipboard).
   Loaded with defer by page-level sections. Exposes window.GB.pages. No dependencies. */
(function () {
  'use strict';
  var GB = (window.GB = window.GB || {});
  if (GB.pages && GB.pages.__ready) return;

  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var $$ = function (sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); };

  var pages = (GB.pages = { __ready: true });

  /* ---------- Clipboard ---------- */
  pages.copy = function (text) {
    if (navigator.clipboard && window.isSecureContext) return navigator.clipboard.writeText(text);
    return new Promise(function (resolve, reject) {
      var ta = document.createElement('textarea');
      ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0'; ta.style.top = '0';
      document.body.appendChild(ta); ta.select(); ta.setSelectionRange(0, text.length);
      var ok = false; try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
      ta.remove(); ok ? resolve() : reject(new Error('copy failed'));
    });
  };

  /* ---------- Slug helper ---------- */
  pages.slug = function (s) {
    return String(s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, '').trim().replace(/\s+/g, '-').replace(/-+/g, '-').slice(0, 60) || 'section';
  };

  /* ---------- Table of contents ---------- */
  var desktopMq = window.matchMedia('(min-width: 990px)');
  pages.toc = function (root) {
    $$('[data-gb-toc]:not([data-init])', root).forEach(function (nav) {
      var source = document.querySelector(nav.getAttribute('data-source'));
      var list = nav.querySelector('[data-gb-toc-list]');
      if (!source || !list) return;
      nav.setAttribute('data-init', '');
      var levels = (nav.getAttribute('data-levels') || 'h2,h3').split(',').map(function (l) { return l.trim(); }).filter(Boolean);
      var min = parseInt(nav.getAttribute('data-min') || '2', 10);
      var heads = $$(levels.join(','), source).filter(function (h) { return h.textContent.trim().length > 0; });
      if (heads.length < min) { nav.hidden = true; return; }

      var used = {};
      list.innerHTML = '';
      var links = heads.map(function (h, i) {
        if (!h.id) {
          var base = pages.slug(h.textContent), id = base, n = 2;
          while (used[id] || document.getElementById(id)) { id = base + '-' + n++; }
          h.id = id;
        }
        used[h.id] = true;
        h.classList.add('gb-toc-target');
        var li = document.createElement('li');
        li.className = 'gb-toc__item gb-toc__item--' + h.tagName.toLowerCase();
        li.style.setProperty('--gb-delay', Math.min(i, 12));
        var a = document.createElement('a');
        a.href = '#' + h.id; a.className = 'gb-toc__link'; a.textContent = h.textContent.trim();
        li.appendChild(a); list.appendChild(li);
        return a;
      });
      nav.hidden = false;

      var details = nav.querySelector('[data-gb-toc-details]');
      var syncOpen = function () { if (details) { if (desktopMq.matches) details.open = true; else if (!details.__touched) details.open = false; } };
      syncOpen();
      if (details) details.addEventListener('toggle', function () { if (!desktopMq.matches) details.__touched = true; });
      if (desktopMq.addEventListener) desktopMq.addEventListener('change', syncOpen);

      list.addEventListener('click', function (e) {
        var a = e.target.closest('a'); if (!a) return;
        var target = document.getElementById(a.getAttribute('href').slice(1)); if (!target) return;
        e.preventDefault();
        target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
        if (history.replaceState) history.replaceState(null, '', '#' + target.id);
        target.setAttribute('tabindex', '-1'); target.focus({ preventScroll: true });
        if (!desktopMq.matches && details) details.open = false;
      });

      if (!('IntersectionObserver' in window)) return;
      var visible = {};
      var setActive = function (id) {
        links.forEach(function (a) {
          var on = a.getAttribute('href') === '#' + id;
          a.classList.toggle('is-active', on);
          if (on) a.setAttribute('aria-current', 'true'); else a.removeAttribute('aria-current');
        });
        nav.style.setProperty('--gb-toc-progress', (heads.findIndex(function (h) { return h.id === id; }) + 1) / heads.length);
      };
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { visible[en.target.id] = en.isIntersecting; });
        var firstVisible = heads.filter(function (h) { return visible[h.id]; })[0];
        if (firstVisible) { setActive(firstVisible.id); return; }
        // None in the reading band: pick the last heading above the viewport.
        var above = heads.filter(function (h) { return h.getBoundingClientRect().top < window.innerHeight * 0.3; }).pop();
        if (above) setActive(above.id);
      }, { rootMargin: '-12% 0px -62% 0px', threshold: 0 });
      heads.forEach(function (h) { io.observe(h); });
    });
  };

  /* ---------- Decorative parallax (transform only, rAF, disabled on touch/reduced motion) ---------- */
  var pxEls = [], ticking = false;
  var pxUpdate = function () {
    ticking = false;
    var vh = window.innerHeight;
    pxEls.forEach(function (el) {
      var r = el.parentNode.getBoundingClientRect();
      if (r.bottom < -200 || r.top > vh + 200) return;
      var speed = parseFloat(el.getAttribute('data-gb-page-parallax')) || 0.08;
      var offset = (r.top + r.height / 2 - vh / 2) * -speed;
      el.style.setProperty('--gb-py', offset.toFixed(1) + 'px');
    });
  };
  var onScroll = function () { if (!ticking) { ticking = true; window.requestAnimationFrame(pxUpdate); } };
  pages.parallax = function (root) {
    if (reduced || !window.matchMedia('(hover: hover) and (min-width: 750px)').matches) return;
    $$('[data-gb-page-parallax]:not([data-init])', root).forEach(function (el) { el.setAttribute('data-init', ''); pxEls.push(el); });
    if (pxEls.length && !pages.__pxBound) {
      pages.__pxBound = true;
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
    }
    pxUpdate();
  };

  /* ---------- Story timeline: draw line on scroll + activate items ---------- */
  var timelines = [], tlTick = false;
  var tlUpdate = function () {
    tlTick = false;
    var vh = window.innerHeight;
    timelines.forEach(function (tl) {
      var r = tl.getBoundingClientRect();
      if (r.bottom < 0 || r.top > vh) {
        var tr = tl.querySelector('[data-gb-timeline-track]'), done = r.top > vh ? 0 : 1;
        tl.style.setProperty('--gb-tl-progress', done);
        if (tr) tl.style.setProperty('--gb-tl-px', (done * tr.offsetHeight) + 'px');
        return;
      }
      var p = (vh * 0.6 - r.top) / Math.max(1, r.height);
      p = Math.max(0, Math.min(1, p));
      tl.style.setProperty('--gb-tl-progress', p.toFixed(4));
      var track = tl.querySelector('[data-gb-timeline-track]');
      if (track) tl.style.setProperty('--gb-tl-px', (p * track.offsetHeight).toFixed(1) + 'px');
      $$('[data-gb-timeline-item]', tl).forEach(function (item) {
        var ir = item.getBoundingClientRect();
        item.classList.toggle('is-active', ir.top < vh * 0.62);
      });
    });
  };
  var tlScroll = function () { if (!tlTick) { tlTick = true; window.requestAnimationFrame(tlUpdate); } };
  pages.timeline = function (root) {
    $$('[data-gb-timeline]:not([data-init])', root).forEach(function (tl) {
      tl.setAttribute('data-init', '');
      if (reduced || !document.documentElement.classList.contains('gb-animate') || window.Shopify && window.Shopify.designMode) {
        tl.style.setProperty('--gb-tl-progress', 1);
        tl.classList.add('is-static');
        $('[data-gb-timeline-item]', tl).forEach(function (i) { i.classList.add('is-active'); });
        return;
      }
      timelines.push(tl);
    });
    if (timelines.length && !pages.__tlBound) {
      pages.__tlBound = true;
      window.addEventListener('scroll', tlScroll, { passive: true });
      window.addEventListener('resize', tlScroll, { passive: true });
    }
    tlUpdate();
  };

  pages.init = function (root) {
    root = root || document;
    pages.toc(root);
    pages.parallax(root);
    pages.timeline(root);
  };

  var boot = function () { pages.init(document); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', function (e) {
    timelines = timelines.filter(function (t) { return document.body.contains(t); });
    pxEls = pxEls.filter(function (t) { return document.body.contains(t); });
    pages.init(e.target);
  });
})();
